import { useEffect } from 'react';

const DEFAULT_IMAGE = 'https://wellzoraherbalcare.com/assets/Logo.jpg';
const SITE_NAME = 'WellZora Herbal Care';

/**
 * Custom hook to dynamically manage document head metadata,
 * Open Graph, Twitter Cards, canonical links, and Schema.org JSON-LD
 * for React SPA routes.
 */
export function usePageSEO({
  title,
  description,
  canonical,
  ogType = 'website',
  ogImage = DEFAULT_IMAGE,
  jsonLd = null,
}) {
  useEffect(() => {
    // 1. Update Document Title
    if (title) {
      document.title = title;
    }

    // Helper to safely set or create meta tag
    const setMetaTag = (attribute, name, content) => {
      if (!content) return;
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // 2. Standard Meta Tags
    if (description) {
      setMetaTag('name', 'description', description);
    }

    // 3. Open Graph Tags
    if (title) setMetaTag('property', 'og:title', title);
    if (description) setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:type', ogType);
    setMetaTag('property', 'og:site_name', SITE_NAME);
    if (canonical) setMetaTag('property', 'og:url', canonical);
    setMetaTag('property', 'og:image', ogImage || DEFAULT_IMAGE);

    // 4. Twitter Card Tags
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    if (title) setMetaTag('name', 'twitter:title', title);
    if (description) setMetaTag('name', 'twitter:description', description);
    setMetaTag('name', 'twitter:image', ogImage || DEFAULT_IMAGE);

    // 5. Canonical Link
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }

    // 6. Route-Specific JSON-LD Structured Data
    const SCRIPT_ID = 'route-dynamic-jsonld';
    let script = document.getElementById(SCRIPT_ID);

    if (jsonLd) {
      if (!script) {
        script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }

    return () => {
      // Clean up route schema on unmount if component changes
      const activeScript = document.getElementById(SCRIPT_ID);
      if (activeScript) {
        activeScript.remove();
      }
    };
  }, [title, description, canonical, ogType, ogImage, jsonLd]);
}
